'use client'

import { useCallback } from 'react'

import VideoThumbnailImage from '@/components/content/videos/VideoThumbnailImage'

import { cn } from '@/lib/utils'
import type { ResolvedVideoApp } from '@/lib/lexical-json/types'
import type { Media } from '@/payload-types'
import { useSegmentPlayer } from './SegmentPlayerContext'
import { Icon } from '@/icons/Icon'
import { getThumbnail } from '@/components/content/videos/helpers'

type Props = {
  videoApp: ResolvedVideoApp
  // Optional override for the visible label. Falls back to the app name from data.
  label?: string
}

function formatDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return ''
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  if (hours > 0) return `${hours}h ${minutes}m`
  if (minutes > 0) return seconds === 0 ? `${minutes}m` : `${minutes}m ${seconds}s`
  return `${seconds}s`
}

// Mirrors VideoSegmentChip but for video_apps_app refs: a junction row labeled
// by the app (which appears in a video over a time range). Reuses the same
// SegmentPlayer context so the play action is consistent with segment chips.
export default function VideoAppChip({ videoApp, label }: Props) {
  const player = useSegmentPlayer()
  const display = label?.trim() || videoApp.appName

  const onClick = useCallback(() => {
    player.play({
      youtubeId: videoApp.youtubeId,
      startSeconds: videoApp.startSeconds,
      // The player API speaks "segment"; we map app-in-video onto those fields
      // so consumers (the popout player) don't need to branch on chip kind.
      segmentName: videoApp.appName,
      segmentId: videoApp.rowId,
      videoName: videoApp.videoName,
      videoId: videoApp.videoId,
      thumbnail: videoApp.thumbnail,
      thumbnailOverride: videoApp.thumbnailOverride,
    })
  }, [player, videoApp])

  // Only show a duration when there's an actual span; a zero/negative range
  // (e.g. fallback rows where endSeconds === startSeconds) would render "0s".
  const duration =
    videoApp.endSeconds > videoApp.startSeconds
      ? formatDuration(videoApp.endSeconds - videoApp.startSeconds)
      : ''

  const thumbnail = getThumbnail({
    youtubeId: videoApp.youtubeId,
    url: '',
    thumbnail: (videoApp.thumbnail ?? undefined) as Media | undefined,
    thumbnailOverride: (videoApp.thumbnailOverride ?? undefined) as Media | undefined,
  })

  return (
    <button
      type="button"
      onClick={onClick}
      title={`Play "${videoApp.appName}" in ${videoApp.videoName}`}
      aria-label={`Play ${videoApp.appName} in ${videoApp.videoName}${duration ? `, ${duration}` : ''}`}
      className={cn(
        'group inline-flex h-fit w-full items-center gap-3',
        'text-slate rounded-xl border border-gray-200 bg-gray-50 text-zinc-600',
        'py-2 pl-2 pr-4 text-14 font-medium',
        'hover:border-primary-500/30 hover:bg-primary-500/10 hover:text-primary-500',
        'dark:border-white/10 dark:bg-neutral-950',
        'cursor-pointer no-underline transition-colors',
      )}
    >
      <div className="relative h-8.5 w-[52px] overflow-hidden rounded-md bg-black">
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-gradient-to-t from-black/75 to-transparent to-75% text-white">
          <Icon name="media-play" />
        </div>
        {thumbnail && (
          <VideoThumbnailImage
            key={thumbnail.url}
            thumbnail={thumbnail}
            quality="small"
            alt={display}
            fill
            sizes="44px"
            className="relative z-10 opacity-80"
          />
        )}
      </div>

      <div className="flex flex-col items-start xs:flex-row xs:items-center xs:gap-1.5">
        <span>Jump to:</span>
        <div className="flex items-center gap-1.5">
          <span className="truncate font-bold text-black transition-colors group-hover:text-primary-500 dark:text-white">
            {display}
          </span>
          {duration && (
            <span className="shrink-0" aria-hidden="true">
              ({duration})
            </span>
          )}
        </div>
      </div>

      <Icon name="pict-in-pict-bottom-right" className="ml-auto" />
    </button>
  )
}
